import { Button, Group, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { Info, Palette, TextSelect } from "lucide-react";
import { useState } from "react";

import { useListAdCopies } from "../../../api/generated/endpoints/generation/generation";
import type { AdCopyTextElements } from "../../../api/generated/model";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import classes from "./ad-copies.module.css";
import { CopyEdit } from "./CopyEdit";
import { CopyView } from "./CopyView";
import { CopyList } from "./CopyList";
import {
  MAX_COMBINATIONS,
  STYLE_TILE,
  keepTiles,
  setPlacement,
  tileCombinations,
  toggleAll,
  toggleValue,
  totalCombinations,
  type Picks,
} from "./picks";

/** Live's words at the cap (`limit-info`), and on an empty pane (`text-copies-empty-state`). */
export const CAP_REACHED = "No more than 100 combinations are allowed.";
export const NO_COPIES = "No text copies available.";

/** How often the list is read while a copy is still written (G8: the fill follows its job). */
const GENERATING_REFETCH_MS = 3_000;

/** A tile of the strip: a reference with its analysed text, or the Style (`STYLE_TILE`). */
export interface Tile {
  id: string;
  url: string | null;
  /** A reference's name, or the Style's text (its tooltip). */
  name: string;
  structure: AdCopyTextElements | null;
}

export interface SavedCopiesProps {
  teamId: string;
  brandId: string;
  productId: string;
  tiles: readonly Tile[];
  /** The ticks the draft holds; the tiles still there start with theirs. */
  confirmed: Picks;
  onConfirm: (picks: Picks) => void;
  onClear: () => void;
}

/**
 * Live's Saved Copies (`saved-copies-tab`, captures advanced-234/12 to 23): the strip of tiles with
 * their counts, the product's copies beside the chosen one, and the footer with live's cap, Clear
 * and Confirm. The ticks stay here until Confirm.
 */
export function SavedCopies(props: SavedCopiesProps) {
  const { teamId, brandId, productId, tiles, confirmed, onConfirm, onClear } = props;
  const ids = tiles.map((tile) => tile.id);
  const [picks, setPicks] = useState<Picks>(() => keepTiles(confirmed, ids));
  const [activeId, setActiveId] = useState(ids[0] ?? STYLE_TILE);
  const [search, setSearch] = useState("");
  const [chosenId, setChosenId] = useState<string | null>(null);
  // Live's Edit of the chosen copy: Confirm and Clear wait, another copy leaves it unsaved.
  const [editing, setEditing] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const copies = useListAdCopies(
    teamId,
    brandId,
    { product_id: productId, ...(search === "" ? {} : { search }) },
    {
      query: {
        staleTime: 0,
        refetchInterval: (query) =>
          query.state.data?.items.some((copy) => copy.status === "in_progress") === true
            ? GENERATING_REFETCH_MS
            : false,
      },
    },
  );
  const items = copies.data?.items ?? [];
  // Live chooses the first copy until another is chosen.
  const chosen = items.find((copy) => copy.id === chosenId) ?? items[0];
  const active = tiles.find((tile) => tile.id === activeId) ?? tiles[0];
  const total = totalCombinations(picks);
  const at = (copyId: string) => ({ tile: active?.id ?? STYLE_TILE, copyId });
  const hasReferences = tiles.some((tile) => tile.id !== STYLE_TILE);
  return (
    <div className={classes.saved}>
      <div className={classes.strip}>
        {tiles.map((tile) => (
          <TileButton
            key={tile.id}
            tile={tile}
            active={tile.id === active?.id}
            count={tileCombinations(picks[tile.id])}
            onChoose={() => setActiveId(tile.id)}
          />
        ))}
      </div>
      <div className={classes.content}>
        <CopyList
          copies={items}
          loading={copies.isPending}
          search={search}
          onSearch={setSearch}
          total={total}
          chosenId={chosen?.id ?? null}
          onChoose={(copy) => {
            setChosenId(copy.id);
            setEditing(false);
          }}
          tile={{ id: active?.id ?? STYLE_TILE, picks: picks[active?.id ?? STYLE_TILE] }}
          collapsed={collapsed}
          onCollapse={setCollapsed}
        />
        {copies.isError ? <ErrorAlert error={copies.error} /> : null}
        {copies.isSuccess && items.length === 0 ? (
          <div className={classes.state}>
            <TextSelect size={40} strokeWidth={1.5} aria-hidden />
            <Text>{NO_COPIES}</Text>
          </div>
        ) : null}
        {chosen !== undefined && editing ? (
          <CopyEdit
            teamId={teamId}
            brandId={brandId}
            copy={chosen}
            onDone={() => setEditing(false)}
          />
        ) : null}
        {chosen === undefined || active === undefined || editing ? null : (
          <CopyView
            // A copy shows afresh on another tile or copy, as live's: the cards it added go.
            key={`${chosen.id} ${active.id}`}
            copy={chosen}
            structure={active.structure}
            hasReferences={hasReferences}
            picks={picks[active.id]?.[chosen.id]}
            onToggleAll={(key, values) =>
              setPicks((current) => toggleAll(current, { ...at(chosen.id), key }, values))
            }
            onToggleValue={(key, value) =>
              setPicks((current) => toggleValue(current, { ...at(chosen.id), key }, value))
            }
            onPlacement={(key, placement) =>
              setPicks((current) => setPlacement(current, { ...at(chosen.id), key }, placement))
            }
            onEdit={() => setEditing(true)}
          />
        )}
      </div>
      <div className={classes.footer}>
        {total >= MAX_COMBINATIONS ? (
          <output className={classes.limit}>
            <Info size={14} aria-hidden />
            {CAP_REACHED}
          </output>
        ) : (
          <span />
        )}
        <Group gap={12}>
          <Button variant="default" disabled={editing} onClick={onClear}>
            Clear
          </Button>
          <Button disabled={total > MAX_COMBINATIONS || editing} onClick={() => onConfirm(picks)}>
            Confirm
          </Button>
        </Group>
      </div>
    </div>
  );
}

interface TileButtonProps {
  tile: Tile;
  active: boolean;
  count: number;
  onChoose: () => void;
}

/** A tile of the strip: the reference's picture, or the Style's, with its count on its corner. */
function TileButton({ tile, active, count, onChoose }: TileButtonProps) {
  const style = tile.id === STYLE_TILE;
  const button = (
    <UnstyledButton
      className={classes.tile}
      data-active={active || undefined}
      aria-pressed={active}
      aria-label={style ? "Style" : tile.name}
      onClick={onChoose}
    >
      {style || tile.url === null ? (
        <span className={`${classes.tilePicture} ${classes.tileStyle}`}>
          <Palette size={20} strokeWidth={1.5} aria-hidden />
        </span>
      ) : (
        <img src={tile.url} alt="" className={classes.tilePicture} />
      )}
      {count > 0 ? (
        <span className={classes.counter} aria-label={`${count} combinations`}>
          {count}
        </span>
      ) : null}
    </UnstyledButton>
  );
  // Live's Style tile says its Style on hover.
  return style ? (
    <Tooltip label={tile.name} position="right" withArrow multiline maw={360}>
      {button}
    </Tooltip>
  ) : (
    button
  );
}
